import { FC, useEffect, useState } from 'react';
import {
  Root,
  Trigger,
  Portal,
  Content,
  Close,
  Arrow,
} from '@radix-ui/react-popover';
import styled from 'styled-components';
import tw from 'twin.macro';

import { ButtonSelect, Button, InputSlider, Chip } from '.';
import { weightItems } from '../constants';
import {
  ButtonColor,
  ButtonSize,
  ChipEnum,
  Icons,
  SoldRefParams,
  Weights,
} from '../enums';
import strings from '../language/Swedish';

const Container = styled.div`
  box-sizing: border-box;
`;

const StyledContent = styled(Content)`
  ${tw`border border-white mt-1.5 backdrop-blur bg-white/80 shadow-xl min-w-[220px] will-change-[transform,opacity] rounded-xl z-20`};
`;

const Popover = styled.div<{ $hasValue: boolean }>`
  ${tw`rounded-lg bg-white/80 shadow-[0_1px_0_0_rgba(255,255,255,0.25)] pt-1 pr-1 flex flex-col`};

  border: 1px solid
    ${({ $hasValue }) => ($hasValue ? 'border-primary' : 'border-slate-200')};
`;

const TopContainer = styled.div`
  ${tw`flex items-center justify-between mb-4 text-slate-500 rounded-t-2xl text-sm pl-2 pb-1`};
`;

const Heading = styled.p`
  ${tw`flex-1 flex items-center gap-3 py-1 m-0 mr-2 text-slate-500 text-sm leading-tight`};
`;

const SubHeading = styled.p`
  ${tw`flex-1 flex items-center gap-3 py-1 m-0 mr-2 text-slate-500 text-xs leading-tight`};
`;

const BottomContainer = styled.div`
  ${tw`flex-1 flex flex-col items-center justify-center gap-2.5 p-2 pb-4`};
`;

const StyledArrow = styled(Arrow)`
  ${tw`fill-white`}
`;

const UnitContainer = styled.span`
  ${tw`text-slate-500 pr-2 text-sm w-auto`}
`;

interface Props {
  refValue?: number;
  weightValue: Weights;
  icon: Icons;
  inputMin: number;
  inputMax: number;
  inputType: ChipEnum;
  keyWeightEl: string;
  keyRefEl?: string;
  unit?: string;
  label: string;
  className?: string;
  onUpdateWeight: (keyEl: string, value: Weights) => void;
  onUpdateRefValue: (keyEl: string, value: number) => void;
  setIsWeightChipOpen: (open:boolean) => void;
}

const WeightChip: FC<Props> = ({
  refValue = 0,
  weightValue = Weights.none,
  icon,
  inputMin,
  inputMax,
  inputType,
  keyWeightEl,
  keyRefEl,
  unit,
  label,
  className,
  onUpdateWeight,
  onUpdateRefValue,
  setIsWeightChipOpen
}) => {
  const [hasValue, setHasValue] = useState<boolean>(
    weightValue || refValue ? true : false
  );
  const [resetValue, setResetValue] = useState<boolean>(false);

  const [hasRefValue, setHasRefValue] = useState<boolean>(
    refValue ? true : false
  );

  useEffect(() => {
    if (hasRefValue && weightValue === 0) {
      onUpdateWeight(keyWeightEl, Weights.medium);
    }
  }, [hasRefValue]);

  const handleReset = () => {
    setHasValue(false);
    setResetValue((prev) => !prev);
    onUpdateWeight(keyWeightEl, Weights.none);
    if (keyRefEl) {
      if (keyRefEl === SoldRefParams.construction_year_reference) {
        // construction is unique as it has a min over 0
        onUpdateRefValue(keyRefEl, 0);
      } else {
        onUpdateRefValue(keyRefEl, inputMin || 0);

        if (inputMin) {
          onUpdateWeight(keyWeightEl, Weights.medium);
        } else {
          setHasRefValue(false);
        }
      }
    }
  };

  const handleWeightChange = (value: string) => {
    const weightValue = parseInt(value, 10);

    const noValue =
      weightValue === Weights.none || weightValue === Weights.medium;

    setHasValue(!noValue);
    onUpdateWeight(keyWeightEl, weightValue);
  };

  const handleRefValueChange = (value: [number]) => {
    if (keyRefEl) {
      onUpdateRefValue(keyRefEl, value[0]);
      setHasRefValue(true);
    }
  };

  useEffect(() => {
    if (weightValue !== Weights.none) {
      setHasValue(true);
    } else if (refValue !== 0) {
      setHasValue(true);
    } else {
      setHasValue(false);
    }
  }, [weightValue, refValue]);

  const getWeightValue = () => {
    if (weightValue === 0) {
      return Weights.none;
    }

    return weightValue;
  };

  const InputComponent = () => {
    switch (inputType) {
      case ChipEnum.select:
        return (
          <div className="flex flex-col w-full col-2">
            <SubHeading>{strings.filter.relevanse}</SubHeading>
            <ButtonSelect
              label={strings.filter.relevanse}
              ariaLabel={strings.filter.relevanse}
              items={weightItems}
              value={getWeightValue()}
              handleValueChange={handleWeightChange}
            />
          </div>
        );
      case ChipEnum.slider:
        return (
          <div className="flex flex-col w-full">
            <InputSlider
              value={[refValue]}
              min={inputMin}
              max={inputMax}
              resetValue={resetValue}
              handleValueChange={handleRefValueChange}>
              {unit && <UnitContainer>{unit}</UnitContainer>}
            </InputSlider>
            <div className="flex flex-col w-full col-2 mt-4">
              <Heading>{strings.filter.relevanse}</Heading>
              <ButtonSelect
                label={strings.filter.relevanse}
                ariaLabel={strings.filter.relevanse}
                items={weightItems}
                value={getWeightValue()}
                handleValueChange={handleWeightChange}
              />
            </div>
          </div>
        );
      default:
        return <></>;
    }
  };

  return (
    <Container className={className}>
      <Root onOpenChange={setIsWeightChipOpen}>
        <Trigger asChild>
          <div>
            <Chip
              icon={icon}
              hasValue={hasValue}
              weightValue={weightValue}
              refValue={refValue}
            />
          </div>
        </Trigger>
        <Portal>
          <StyledContent side="top" align="start">
            <Popover $hasValue={hasValue}>
              <TopContainer>
                {label && <Heading>{label}</Heading>}
                <Button
                  onClick={handleReset}
                  color={ButtonColor.transparent}
                  size={ButtonSize.large}
                  icon={Icons.trashcan}
                  className='z-1'
                />
                <Close asChild aria-aria-label="Reset">
                  <Button
                    color={ButtonColor.transparent}
                    size={ButtonSize.large}
                    icon={Icons.remove}
                  />
                </Close>
              </TopContainer>
              <BottomContainer>{InputComponent()}</BottomContainer>
            </Popover>
            <StyledArrow />
          </StyledContent>
        </Portal>
      </Root>
    </Container>
  );
};

export default WeightChip;
